// ============================================================================
// Nutrition API — TRAINER-side views of a client's intake + targets (Wave 2).
// ----------------------------------------------------------------------------
// Read-only for the trainer: a client owns (and logs) their own meals/water via
// the portal; the trainer observes them here.
//   • `forClient`  — an owned client's nutrition: auto targets + day's meals +
//     macro totals + daily water rows (GET /api/clients/{id}/nutrition).
//   • `mealsForClient` — just the day's meals + totals (GET /api/clients/{id}/meals).
//   • `calendar`   — per-day total-kcal meal markers across every owned client
//     (GET /api/meals/calendar), the amber dots of the trainer calendar merge.
// Client self-service nutrition lives in api/client.ts.
// ============================================================================
import { http } from "./http";
import type {
  ClientNutrition,
  MealCalendarMarker,
  MealListResponse,
} from "./types";

/** Inclusive [start,end] day bounds for the trainer meal-calendar feed. */
export interface MealCalendarRange {
  start?: string;
  end?: string;
  // Index signature keeps this assignable to the http `Query` param type.
  [key: string]: string | undefined;
}

export const nutritionApi = {
  /** An owned client's nutrition (targets + day's meals + totals + water). */
  forClient: (clientId: number, day?: string, signal?: AbortSignal) =>
    http.get<ClientNutrition>(
      `/clients/${clientId}/nutrition`,
      { day },
      signal,
    ),

  /** An owned client's day meals + macro totals (`?day=YYYY-MM-DD` filters). */
  mealsForClient: (clientId: number, day?: string, signal?: AbortSignal) =>
    http.get<MealListResponse>(`/clients/${clientId}/meals`, { day }, signal),

  /** Per-day total-kcal meal markers across ALL of THIS trainer's clients. */
  calendar: (range: MealCalendarRange = {}, signal?: AbortSignal) =>
    http.get<MealCalendarMarker[]>("/meals/calendar", range, signal),
};
